/**
 * Détruire une affaire — et tout ce qui retient la main avant.
 *
 * **On montre avant de demander de confirmer.** « Supprimer l'affaire ? » ne
 * dit rien ; « 14 pièces, 22 révisions, 613 Mo » dit ce qu'on perd. C'est la
 * seule façon qu'une confirmation soit un choix plutôt qu'un réflexe.
 *
 * **La référence doit être recopiée.** Une case « je confirme » se coche sans
 * lire ; recopier `30429` demande de regarder laquelle on détruit. La
 * destruction n'a pas de retour arrière — c'est la seule friction du portail
 * qui soit voulue.
 *
 * **Les obstacles sont dits d'emblée**, non au refus. Faire saisir une
 * référence pour buter ensuite sur « cette affaire a produit des missions »
 * serait une perte de temps doublée d'une inquiétude.
 */
import { useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { Avis } from "../../composants/avis";
import { Chargement, Echec } from "../../composants/etats";
import { poids } from "../../composants/formats";
import { useDonnees } from "../../composants/donnees";
import { useModale } from "../../composants/clavier";
import { naviguer } from "../../routeur";

interface Inventaire {
  affaire_id: string;
  reference: string;
  etat: string;
  pieces: number;
  revisions: number;
  octets: number;
  instantanes: number;
  obstacles: string[];
  avertissements: string[];
}

export function Supprimer({
  affaireId,
  fermer,
}: {
  affaireId: string;
  fermer: () => void;
}) {
  const [saisie, poserSaisie] = useState("");
  const [renonce, poserRenonce] = useState(false);
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");

  // Modale complète : Échap ferme, focus piégé et rendu — sauf pendant l'envoi,
  // où l'on ne quitte pas une destruction en cours d'un coup de touche.
  const refModale = useModale(!envoi, fermer);

  const inventaire = useDonnees<{ donnees: Inventaire }>(
    () => api.lire(`/api/v1/affaires/${affaireId}/suppression`),
    [affaireId],
  );

  const vue = inventaire.donnees?.donnees;
  // **Un obstacle arrête ; un avertissement se reconnaît.** Une mission qui a
  // tourné ne se renonce pas — la preuve d'un chiffrage ne se détruit pas sur
  // décision. Une conservation en cours promet une disponibilité future, non
  // une preuve passée : l'administrateur peut y renoncer, à découvert.
  const retenue = (vue?.obstacles.length ?? 0) > 0;
  const avertissements = vue?.avertissements ?? [];
  const reconnu = avertissements.length === 0 || renonce;
  const concorde = vue !== undefined && saisie.trim() === vue.reference;

  async function detruire() {
    if (!vue) return;
    poserErreur("");
    poserEnvoi(true);
    try {
      await api.ecrire(`/api/v1/affaires/${affaireId}/suppression`, {
        reference: saisie.trim(),
        renoncer_a_la_conservation: renonce,
      });
      // Retour à la liste : rester sur l'affaire détruite afficherait un 404
      // dont on croirait qu'il vient d'échouer.
      naviguer("/affaires");
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "La suppression n'a pas abouti.",
      );
      poserEnvoi(false);
    }
  }

  return (
    <div ref={refModale} className="voile" role="dialog" aria-modal="true"
         aria-label="Supprimer l'affaire">
      <div className="modale" style={{ width: "min(560px, 100%)" }}>
        <div className="modale__tete">
          <h2>Supprimer cette affaire ?</h2>
        </div>

        <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
          {inventaire.charge && !inventaire.donnees ? (
            <Chargement lignes={3} />
          ) : inventaire.erreur ? (
            <Echec erreur={inventaire.erreur} reessayer={inventaire.recharger} />
          ) : vue ? (
            <div className="pile">
              {retenue ? (
                <Avis ton="attention" permanent>
                  <strong>Cette affaire ne peut pas être supprimée.</strong>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {vue.obstacles.map((obstacle) => (
                      <li key={obstacle}>{obstacle}</li>
                    ))}
                  </ul>
                </Avis>
              ) : (
                <Avis ton="erreur" permanent>
                  <strong>Rien de ceci n'est récupérable.</strong> Les fichiers,
                  leurs révisions, l'index et l'espace de travail sont détruits.
                  Pour conserver l'affaire sans la voir, archivez-la.
                </Avis>
              )}

              <dl className="fiche">
                <div><dt>Référence</dt>
                  <dd className="mono">{vue.reference}</dd></div>
                <div><dt>Pièces</dt><dd>{vue.pieces}</dd></div>
                <div><dt>Révisions</dt>
                  <dd>{vue.revisions} · {poids(vue.octets)}</dd></div>
                <div><dt>Instantanés</dt><dd>{vue.instantanes}</dd></div>
              </dl>

              {!retenue && avertissements.length > 0 && (
                <label className="renoncement">
                  <input
                    type="checkbox"
                    checked={renonce}
                    onChange={(evenement) => poserRenonce(evenement.target.checked)}
                  />
                  <span>
                    {avertissements.join(" ")} <strong>J'y renonce.</strong>
                  </span>
                </label>
              )}

              {!retenue && (
                <label className="champ">
                  <span>
                    Recopiez <strong className="mono">{vue.reference}</strong>{" "}
                    pour confirmer.
                  </span>
                  <input
                    value={saisie}
                    autoFocus
                    aria-label="Référence de l'affaire à supprimer"
                    placeholder={vue.reference}
                    onChange={(evenement) => poserSaisie(evenement.target.value)}
                  />
                </label>
              )}

              {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
            </div>
          ) : null}
        </div>

        <div className="modale__pied">
          <span style={{ flex: 1 }} />
          <button type="button" className="bouton" onClick={fermer}>
            Annuler
          </button>
          <button
            type="button"
            className="bouton bouton--danger"
            disabled={!concorde || retenue || !reconnu || envoi}
            onClick={() => void detruire()}
          >
            {envoi ? "Suppression…" : "Supprimer définitivement"}
          </button>
        </div>
      </div>
    </div>
  );
}
